import type { Program } from '@vta/types';

/**
 * Formatting helpers shared across detail pages, drawers, and search.
 *
 * Casing rule (PRD §6.5): team numbers are case-significant identifiers.
 * `839A`, `839a`, and `839 a` are visibly different — preserve exactly what
 * the API returns. Trim only outer whitespace; never lowercase or upper-case.
 */
export function formatTeamNumber(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw.trim();
}

const PROGRAM_LABELS: Record<Program, string> = {
  v5rc: 'VEX V5 Robotics Competition',
  viqrc: 'VEX IQ Robotics Competition',
  vurc: 'VEX U Robotics Competition',
  vairc: 'VEX AI Robotics Competition',
  unknown: 'Unknown program',
};

const PROGRAM_SHORT: Record<Program, string> = {
  v5rc: 'V5RC',
  viqrc: 'VIQRC',
  vurc: 'VURC',
  vairc: 'VAIRC',
  unknown: '—',
};

export function formatProgramLabel(program: Program | null | undefined, mode: 'long' | 'short' = 'short'): string {
  if (!program) return mode === 'short' ? '—' : 'Unknown program';
  return mode === 'short' ? PROGRAM_SHORT[program] : PROGRAM_LABELS[program];
}

const RTF = typeof Intl !== 'undefined' ? new Intl.RelativeTimeFormat('en', { numeric: 'auto' }) : null;

const RELATIVE_UNITS: Array<{ unit: Intl.RelativeTimeFormatUnit; ms: number }> = [
  { unit: 'year', ms: 1000 * 60 * 60 * 24 * 365 },
  { unit: 'month', ms: 1000 * 60 * 60 * 24 * 30 },
  { unit: 'week', ms: 1000 * 60 * 60 * 24 * 7 },
  { unit: 'day', ms: 1000 * 60 * 60 * 24 },
  { unit: 'hour', ms: 1000 * 60 * 60 },
  { unit: 'minute', ms: 1000 * 60 },
  { unit: 'second', ms: 1000 },
];

/**
 * Formats an ISO timestamp as a relative phrase ("3 days ago", "in 2 hours").
 * Returns an empty string for null/invalid input rather than throwing — this
 * is rendered next to "last synced" labels which are already permitted to be
 * absent per PRD §7.1.9.
 */
export function formatRelativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const target = new Date(iso);
  const diffMs = target.getTime() - now.getTime();
  if (Number.isNaN(diffMs)) return '';
  for (const { unit, ms } of RELATIVE_UNITS) {
    if (Math.abs(diffMs) >= ms || unit === 'second') {
      const value = Math.round(diffMs / ms);
      if (RTF) return RTF.format(value, unit);
      const abs = Math.abs(value);
      const suffix = value < 0 ? 'ago' : 'from now';
      return `${abs} ${unit}${abs === 1 ? '' : 's'} ${suffix}`;
    }
  }
  return '';
}

const PROGRAM_BADGE_VARIANT: Record<Program, 'v5rc' | 'viqrc' | 'vurc' | 'vairc' | 'neutral'> = {
  v5rc: 'v5rc',
  viqrc: 'viqrc',
  vurc: 'vurc',
  vairc: 'vairc',
  unknown: 'neutral',
};

export function programBadgeVariant(program: Program | null | undefined) {
  if (!program) return 'neutral' as const;
  return PROGRAM_BADGE_VARIANT[program];
}
